import * as THREE from "three";
import { VOXEL_SIZE } from "./VoxelInstanceBuffer";
import { SheepRenderer } from "./assets/VoxelSheep";
import type { SheepStyle } from "./assets/catalog/PetCatalog";

/**
 * The flock in the pasture west of the river.
 *
 * Sheep are simpler than the villagers: no routes and no bridge to climb, just a short line
 * each walks back and forth along, with a long stop at either end to crop the grass. The
 * grazing pause is most of a sheep's life on purpose — a pen of animals all pacing at once
 * reads as agitated rather than pastoral.
 */

/** Where a sheep grazes, in voxel coordinates. */
export type SheepPath = {
  style: SheepStyle;
  start: { x: number; y: number; z: number };
  end: { x: number; y: number; z: number };
};

/** Lifts a sheep so its hooves sit on top of the surface voxel rather than inside it. */
const FOOT_LIFT = 0.5 * VOXEL_SIZE + 0.05;

type Grazer = {
  renderer: SheepRenderer;
  start: THREE.Vector3;
  end: THREE.Vector3;
  progress: number;
  direction: number;
  speed: number;
  grazing: boolean;
  grazeFor: number;
};

/** A sheep looks up from the grass for this long, give or take, before settling again. */
function grazeSpell(): number {
  return 5 + Math.random() * 8;
}

export class SheepFlock {
  private flock: Grazer[] = [];

  constructor(scene: THREE.Scene, paths: SheepPath[]) {
    paths.forEach((path, index) => {
      const renderer = new SheepRenderer(path.style);
      scene.add(renderer.group);

      const start = new THREE.Vector3(
        path.start.x * VOXEL_SIZE,
        path.start.y * VOXEL_SIZE + FOOT_LIFT,
        path.start.z * VOXEL_SIZE
      );
      const end = new THREE.Vector3(
        path.end.x * VOXEL_SIZE,
        path.end.y * VOXEL_SIZE + FOOT_LIFT,
        path.end.z * VOXEL_SIZE
      );

      // Spread the flock along their lines and stagger who is walking, so they never move as
      // one body.
      const progress = (index * 0.18 + Math.random() * 0.2) % 1;
      const direction = index % 2 === 0 ? 1 : -1;
      const grazing = index % 2 !== 0;

      renderer.group.position.lerpVectors(start, end, progress);
      renderer.group.lookAt(direction > 0 ? end : start);
      // A grazing sheep has no reason to be square to its path.
      if (grazing) renderer.group.rotation.y += (Math.random() - 0.5) * 1;

      this.flock.push({
        renderer,
        start,
        end,
        progress,
        direction,
        speed: 0.3 + Math.random() * 0.2,
        grazing,
        grazeFor: grazeSpell()
      });
    });
  }

  public update(time: number, delta: number): void {
    for (const sheep of this.flock) {
      if (sheep.grazing) {
        sheep.grazeFor -= delta;
        if (sheep.grazeFor <= 0) {
          sheep.grazing = false;
          sheep.direction *= -1;
          sheep.renderer.group.lookAt(sheep.direction > 0 ? sheep.end : sheep.start);
        }
      } else {
        const span = sheep.start.distanceTo(sheep.end);
        sheep.progress += ((sheep.speed * delta) / span) * sheep.direction;
        sheep.renderer.group.lookAt(sheep.direction > 0 ? sheep.end : sheep.start);

        if (sheep.progress >= 1 || sheep.progress <= 0) {
          sheep.progress = Math.min(1, Math.max(0, sheep.progress));
          sheep.grazing = true;
          sheep.grazeFor = grazeSpell();
          sheep.renderer.group.rotation.y += (Math.random() - 0.5) * 0.9;
        }

        sheep.renderer.group.position.lerpVectors(sheep.start, sheep.end, sheep.progress);
      }

      sheep.renderer.update(time, delta, sheep.grazing ? "grazing" : "walking");
    }
  }

  public destroy(scene: THREE.Scene): void {
    for (const sheep of this.flock) {
      scene.remove(sheep.renderer.group);
      sheep.renderer.dispose();
    }
    this.flock = [];
  }
}
